import { useState, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Calendar, MapPin, Loader2, Check, Ship } from 'lucide-react';
import { parseISO } from 'date-fns';
import { Notification } from '../types/friends';
import { Cruise, useCruises } from '../context/CruiseContext';
import { useFriends } from '../context/FriendContext';
import { useDisplayPreferences } from '../hooks/useDisplayPreferences';
import { checkCruiseConflict } from '../utils/dateUtils';
import CruiseConflictModal from './CruiseConflictModal';

interface AcceptCruiseInviteModalProps {
  notification: Notification | null;
  isOpen: boolean;
  onClose: () => void;
  onAccepted?: (newCruiseId: string) => void;
}

const AcceptCruiseInviteModal = ({
  notification,
  isOpen,
  onClose,
  onAccepted,
}: AcceptCruiseInviteModalProps) => {
  const { acceptCruiseInvite, deleteNotification } = useFriends();
  const { cruises } = useCruises();
  const { formatDate } = useDisplayPreferences();

  const [loading, setLoading] = useState(false);
  const [declineLoading, setDeclineLoading] = useState(false);
  const [showConflictModal, setShowConflictModal] = useState(false);
  const [conflictingCruise, setConflictingCruise] = useState<Cruise | null>(null);

  if (!isOpen || !notification || notification.type !== 'cruise-invite') return null;

  const cruiseData = notification.invitedCruiseData;

  const formattedDates = useMemo(() => {
    if (!cruiseData?.departureDate || !cruiseData?.returnDate) return null;
    try {
      const start = parseISO(cruiseData.departureDate);
      const end = parseISO(cruiseData.returnDate);
      return `${formatDate(start)} — ${formatDate(end)}`;
    } catch {
      return `${cruiseData.departureDate} — ${cruiseData.returnDate}`;
    }
  }, [cruiseData?.departureDate, cruiseData?.returnDate, formatDate]);

  const proceedWithAccept = async () => {
    try {
      setLoading(true);
      const newCruiseId = await acceptCruiseInvite(notification);
      onClose();
      if (onAccepted) onAccepted(newCruiseId);
    } catch (error) {
      console.error('Failed to accept cruise invite:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = () => {
    if (!cruiseData) return;

    if (cruiseData.departureDate && cruiseData.returnDate) {
      try {
        const conflicting = checkCruiseConflict({
          departureDate: cruiseData.departureDate,
          returnDate: cruiseData.returnDate,
          existingCruises: cruises,
        });

        if (conflicting) {
          setConflictingCruise(conflicting);
          setShowConflictModal(true);
          return;
        }
      } catch (err) {
        console.warn('Could not check cruise conflict:', err);
      }
    }

    proceedWithAccept();
  };

  const handleConflictConfirm = () => {
    setShowConflictModal(false);
    setConflictingCruise(null);
    proceedWithAccept();
  };

  const handleConflictCancel = () => {
    setShowConflictModal(false);
    setConflictingCruise(null);
  };

  const handleDecline = async () => {
    try {
      setDeclineLoading(true);
      await deleteNotification(notification.id);
      onClose();
    } catch (error) {
      console.error('Failed to decline cruise invite:', error);
    } finally {
      setDeclineLoading(false);
    }
  };

  return (
    <>
      {createPortal(
        <AnimatePresence>
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-lg shadow-xl max-w-md w-full overflow-hidden flex flex-col"
            >
              {/* Header */}
              <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gradient-to-r from-teal-50 to-blue-50">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full bg-secondary-600 flex items-center justify-center text-white font-semibold">
                    {notification.fromDisplayName.charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900">Cruise Invitation</h2>
                    <p className="text-xs text-gray-600">
                      {notification.fromDisplayName} invited you to join them on this cruise!
                    </p>
                  </div>
                </div>
                <button
                  onClick={onClose}
                  className="text-gray-400 hover:text-gray-600 transition-colors p-1"
                  aria-label="Close"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              {/* Cruise Details Body */}
              <div className="p-6 space-y-4">
                {cruiseData ? (
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-3">
                    <div className="flex items-start gap-3">
                      <Ship className="h-6 w-6 text-secondary-600 shrink-0 mt-0.5" />
                      <div>
                        <h3 className="font-bold text-gray-900 text-lg">{cruiseData.shipName}</h3>
                        <p className="text-sm text-gray-600">{cruiseData.cruiseLine}</p>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 text-sm text-gray-700 pt-2 border-t border-gray-200">
                      <Calendar className="h-4 w-4 text-teal-600 shrink-0" />
                      <span>
                        {formattedDates || `${cruiseData.departureDate} — ${cruiseData.returnDate}`}
                      </span>
                    </div>

                    {cruiseData.departurePort && (
                      <div className="flex items-center gap-2 text-sm text-gray-700">
                        <MapPin className="h-4 w-4 text-teal-600 shrink-0" />
                        <span>Departs from {cruiseData.departurePort}</span>
                      </div>
                    )}

                    {cruiseData.ports && cruiseData.ports.length > 0 && (
                      <div className="text-xs text-gray-500 pt-1">
                        <span className="font-medium text-gray-700">Ports of Call: </span>
                        {cruiseData.ports.map((p) => p.name).join(', ')}
                      </div>
                    )}
                  </div>
                ) : (
                  <p className="text-sm text-gray-600">
                    {notification.message}
                  </p>
                )}

                <p className="text-xs text-gray-500">
                  Accepting will load this cruise itinerary directly into your app. Personal booking or cabin details are not included.
                </p>
              </div>

              {/* Actions */}
              <div className="p-4 border-t border-gray-200 bg-gray-50 flex gap-3">
                <button
                  onClick={handleDecline}
                  disabled={declineLoading || loading}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md text-sm font-medium hover:bg-gray-100 transition-colors disabled:opacity-50"
                >
                  {declineLoading ? <Loader2 className="h-4 w-4 animate-spin mx-auto" /> : 'Decline'}
                </button>
                <button
                  onClick={handleAccept}
                  disabled={loading || declineLoading}
                  className="flex-1 px-4 py-2 bg-secondary-600 hover:bg-secondary-500 text-white rounded-md text-sm font-medium transition-colors disabled:opacity-50 flex items-center justify-center gap-1.5"
                >
                  {loading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <>
                      <Check className="h-4 w-4" />
                      Accept Cruise
                    </>
                  )}
                </button>
              </div>
            </motion.div>
          </div>
        </AnimatePresence>,
        document.body
      )}

      {/* Date Conflict Modal */}
      {showConflictModal && conflictingCruise && cruiseData && (
        <CruiseConflictModal
          isOpen={showConflictModal}
          onClose={handleConflictCancel}
          onConfirm={handleConflictConfirm}
          conflictingCruise={conflictingCruise}
          newCruiseDates={{
            departureDate: cruiseData.departureDate,
            returnDate: cruiseData.returnDate,
            shipName: cruiseData.shipName,
            cruiseLine: cruiseData.cruiseLine,
          }}
          cancelText="Cancel"
          confirmText="Continue Anyway"
        />
      )}
    </>
  );
};

export default AcceptCruiseInviteModal;
